import { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AMCReportTable from "./AMCReportTable";
import { metricColors } from "components/custom/analytics/palettes";
import { token } from "../../../styled-system/tokens";
import { time_to_conversion_metrics, type TimeToConversionMetrics } from "./utils";

interface ConversionTimeDataItem {
  time_to_conversion: TimeToConversionMetrics;
  campaign: string;
  total_brand_purchases: number;
  purchases: number;
}

interface Props {
  conversionData: ConversionTimeDataItem[];
}

const ConversionHistogram = ({ conversionData }: Props) => {
  const chartOptions = useMemo((): Highcharts.Options => {
    const timeBucketTotals: Record<string, number> = {};

    conversionData.forEach(({ time_to_conversion, purchases }) => {
      timeBucketTotals[time_to_conversion] = (timeBucketTotals[time_to_conversion] || 0) + purchases;
    });

    const sortedTimeBuckets = time_to_conversion_metrics.filter((bucket) => Object.hasOwn(timeBucketTotals, bucket));

    return {
      chart: {
        type: "column",
        height: 400,
        style: {
          fontSize: token("fontSizes.chart"),
          fontFamily: "Poppins",
          fontWeight: "300",
        },
      },
      title: {
        text: "",
      },
      xAxis: {
        categories: sortedTimeBuckets,
        title: {
          text: "Time to Conversion",
        },
      },
      yAxis: {
        title: {
          text: "Total Purchases",
        },
        min: 0,
      },
      tooltip: {
        shared: true,
        valueSuffix: " purchases",
      },
      plotOptions: {
        column: {
          stacking: "normal",
        },
      },
      series: [
        {
          type: "column",
          name: "Total Purchases",
          data: sortedTimeBuckets.map((bucket) => timeBucketTotals[bucket] || 0),
          color: metricColors.units[0],
        },
      ],
      credits: {
        enabled: false,
      },
    };
  }, [conversionData]);

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

interface ConversionTimeProps {
  reportData: ConversionTimeDataItem[];
  reportDetails: ConversionTimeDataItem[];
  queryDetails: ConversionTimeDataItem[];
}
type PivotedRow = {
  campaign: string;
} & {
  [key in TimeToConversionMetrics]?: number;
};

function pivotConversionData(data: ConversionTimeDataItem[]): PivotedRow[] {
  const campaignMap: Record<string, PivotedRow> = {};

  data.forEach(({ campaign, time_to_conversion, purchases }) => {
    if (!(campaign in campaignMap)) {
      campaignMap[campaign] = { campaign };
    }

    const campaignRow = campaignMap[campaign]!;
    const key = time_to_conversion;
    const prev = campaignRow[key];

    campaignRow[key] = (typeof prev === "number" ? prev : 0) + purchases;
  });

  return Object.values(campaignMap);
}

const AMCTimeToConversion = ({ reportData, reportDetails, queryDetails }: ConversionTimeProps) => {
  return (
    <>
      <ConversionHistogram conversionData={reportData} />

      <AMCReportTable
        reportData={pivotConversionData(reportData)}
        reportDetails={reportDetails}
        queryDetails={queryDetails}
      />
    </>
  );
};

export default AMCTimeToConversion;
